import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Package, Loader2, Edit, Image as ImageIcon, X, PlusCircle, Trash2, Upload, Copy } from "lucide-react";

export default function ItemsPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    description: "",
    category_id: "",
    subcategory_id: "",
    customer_id: "",
    customer_item_number: "",
    artwork_owner: "",
    image_url: "",
    remarks: "",
    specifications: {
      length: "",
      width: "",
      height: "",
      gsm: "",
      material: "",
      color: "",
      printing_type: "",
      finish: "",
      handle_type: "",
      quantity_per_carton: "",
    },
    custom_fields: {},
  });

  const [newCustomField, setNewCustomField] = useState({ key: "", value: "" });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/items`, { withCredentials: true }),
      axios.get(`${API}/categories`, { withCredentials: true }),
      axios.get(`${API}/customers`, { withCredentials: true }),
    ]).then(([itemsRes, catsRes, custsRes]) => {
      setItems(itemsRes.data);
      setCategories(catsRes.data);
      setCustomers(custsRes.data);
    }).catch((err) => console.error("Failed to fetch data:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, GIF, WebP");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max size: 5MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Set the image URL (relative URL from backend)
      setFormData((prev) => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded successfully");
    } catch (err) {
      console.error("Failed to upload image:", err);
      toast.error(err.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      sku: "",
      description: "",
      category_id: "",
      subcategory_id: "",
      customer_id: "",
      customer_item_number: "",
      artwork_owner: "",
      image_url: "",
      remarks: "",
      specifications: {
        length: "",
        width: "",
        height: "",
        gsm: "",
        material: "",
        color: "",
        printing_type: "",
        finish: "",
        handle_type: "",
        quantity_per_carton: "",
      },
      custom_fields: {},
    });
    setNewCustomField({ key: "", value: "" });
  };

  const handleDeleteItem = async (itemId, itemName) => {
    setDeleting(itemId);
    try {
      await axios.delete(`${API}/items/${itemId}`, { withCredentials: true });
      toast.success(`Item "${itemName}" deleted successfully`);
      setItems((prev) => prev.filter((i) => i.item_id !== itemId));
    } catch (err) {
      console.error("Failed to delete item:", err);
      toast.error(err.response?.data?.detail || "Failed to delete item");
    } finally {
      setDeleting(null);
    }
  };

  const handleRequestDeletion = async (itemId, itemName) => {
    setDeleting(itemId);
    try {
      await axios.post(`${API}/deletion-requests`, {
        entity_type: "item",
        entity_id: itemId,
        entity_name: itemName,
      });
      toast.success("Deletion request submitted for approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit deletion request");
    } finally {
      setDeleting(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.sku) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.category_id) {
      toast.error("Category is mandatory");
      return;
    }

    if (!formData.customer_id) {
      toast.error("Customer is mandatory");
      return;
    }

    setSubmitting(true);

    try {
      // Clean specifications - only include non-empty values
      const specs = {};
      Object.entries(formData.specifications).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          specs[key] = isNaN(value) ? value : parseFloat(value);
        }
      });

      const payload = {
        name: formData.name,
        sku: formData.sku,
        description: formData.description || null,
        category_id: formData.category_id || null,
        subcategory_id: formData.subcategory_id || null,
        customer_id: formData.customer_id,
        customer_item_number: formData.customer_item_number || null,
        artwork_owner: formData.artwork_owner || null,
        image_url: formData.image_url || null,
        remarks: formData.remarks || null,
        specifications: Object.keys(specs).length > 0 ? specs : null,
        custom_fields: Object.keys(formData.custom_fields).length > 0 ? formData.custom_fields : null,
      };

      const response = await axios.post(`${API}/items`, payload, {
        withCredentials: true,
      });
      setItems((prev) => [response.data, ...prev]);
      toast.success("Item created successfully");
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error("Failed to create item:", err);
      toast.error(err.response?.data?.detail || "Failed to create item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      sku: item.sku || "",
      description: item.description || "",
      category_id: item.category_id || "",
      subcategory_id: item.subcategory_id || "",
      customer_id: item.customer_id || "",
      customer_item_number: item.customer_item_number || "",
      artwork_owner: item.artwork_owner || "",
      image_url: item.image_url || "",
      remarks: item.remarks || "",
      specifications: {
        length: item.specifications?.length || "",
        width: item.specifications?.width || "",
        height: item.specifications?.height || "",
        gsm: item.specifications?.gsm || "",
        material: item.specifications?.material || "",
        color: item.specifications?.color || "",
        printing_type: item.specifications?.printing_type || "",
        finish: item.specifications?.finish || "",
        handle_type: item.specifications?.handle_type || "",
        quantity_per_carton: item.specifications?.quantity_per_carton || "",
      },
      custom_fields: item.custom_fields || {},
    });
    setEditDialogOpen(true);
  };

  const handleDuplicateItem = (item) => {
    setFormData({
      name: `${item.name || ""} (Copy)`,
      sku: "",
      description: item.description || "",
      category_id: item.category_id || "",
      subcategory_id: item.subcategory_id || "",
      customer_id: item.customer_id || "",
      customer_item_number: item.customer_item_number || "",
      artwork_owner: item.artwork_owner || "",
      image_url: item.image_url || "",
      remarks: item.remarks || "",
      specifications: {
        length: item.specifications?.length || "",
        width: item.specifications?.width || "",
        height: item.specifications?.height || "",
        gsm: item.specifications?.gsm || "",
        material: item.specifications?.material || "",
        color: item.specifications?.color || "",
        printing_type: item.specifications?.printing_type || "",
        finish: item.specifications?.finish || "",
        handle_type: item.specifications?.handle_type || "",
        quantity_per_carton: item.specifications?.quantity_per_carton || "",
      },
      custom_fields: item.custom_fields ? { ...item.custom_fields } : {},
    });
    setDialogOpen(true);
    toast.info("Item data copied. Update the name and SKU, then save.");
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    if (!editingItem) return;

    setSubmitting(true);

    try {
      // Clean specifications
      const specs = {};
      Object.entries(formData.specifications).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          specs[key] = isNaN(value) ? value : parseFloat(value);
        }
      });

      const payload = {
        name: formData.name,
        sku: formData.sku,
        description: formData.description || null,
        category_id: formData.category_id || null,
        subcategory_id: formData.subcategory_id || null,
        customer_id: formData.customer_id || null,
        customer_item_number: formData.customer_item_number || null,
        artwork_owner: formData.artwork_owner || null,
        image_url: formData.image_url || null,
        remarks: formData.remarks || null,
        specifications: Object.keys(specs).length > 0 ? specs : null,
        custom_fields: Object.keys(formData.custom_fields).length > 0 ? formData.custom_fields : null,
      };

      await axios.put(`${API}/items/${editingItem.item_id}`, payload, {
        withCredentials: true,
      });

      // Update local state
      setItems((prev) =>
        prev.map((item) =>
          item.item_id === editingItem.item_id
            ? { ...item, ...payload }
            : item
        )
      );

      toast.success("Item updated successfully");
      setEditDialogOpen(false);
      setEditingItem(null);
      resetForm();
    } catch (err) {
      console.error("Failed to update item:", err);
      toast.error(err.response?.data?.detail || "Failed to update item");
    } finally {
      setSubmitting(false);
    }
  };

  const addCustomField = () => {
    if (!newCustomField.key.trim()) {
      toast.error("Please enter a field name");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [newCustomField.key]: newCustomField.value,
      },
    }));
    setNewCustomField({ key: "", value: "" });
  };

  const removeCustomField = (key) => {
    setFormData((prev) => {
      const newFields = { ...prev.custom_fields };
      delete newFields[key];
      return { ...prev, custom_fields: newFields };
    });
  };

  const filteredItems = items.filter(
    (item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.sku.toLowerCase().includes(search.toLowerCase()) ||
        (item.customer_item_number || "").toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === "all" || item.category_id === categoryFilter;
      return matchesSearch && matchesCategory;
    }
  );

  // Render form fields (shared between create and edit)
  const renderFormFields = () => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Item Name *</Label>
          <Input
            id="name"
            placeholder="e.g., Paper Bag Large"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            className="rounded-sm"
            data-testid="item-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sku">SKU *</Label>
          <Input
            id="sku"
            placeholder="e.g., PB-001"
            value={formData.sku}
            onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
            className="rounded-sm"
            data-testid="item-sku-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select
            value={formData.category_id || "none"}
            onValueChange={(value) => setFormData((prev) => ({
              ...prev,
              category_id: value === "none" ? "" : value,
              subcategory_id: "",
            }))}
          >
            <SelectTrigger className="rounded-sm" data-testid="item-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Category</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id}>
                  {cat.name} ({cat.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Subcategory</Label>
          <Select
            value={formData.subcategory_id || "none"}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, subcategory_id: value === "none" ? "" : value }))}
            disabled={!formData.category_id}
          >
            <SelectTrigger className="rounded-sm" data-testid="item-subcategory-select">
              <SelectValue placeholder={formData.category_id ? "Select subcategory" : "Select category first"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {(categories.find((c) => c.category_id === formData.category_id)?.subcategories || []).map((sub) => (
                <SelectItem key={sub.subcategory_id} value={sub.subcategory_id}>
                  {sub.name} ({sub.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Customer *</Label>
          <Select
            value={formData.customer_id || "none"}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, customer_id: value === "none" ? "" : value }))}
          >
            <SelectTrigger className="rounded-sm" data-testid="item-customer-select">
              <SelectValue placeholder="Select customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select customer</SelectItem>
              {customers.map((cust) => (
                <SelectItem key={cust.customer_id} value={cust.customer_id}>
                  {cust.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Customer Item Number</Label>
          <Input
            placeholder="e.g., CIN-12345"
            value={formData.customer_item_number}
            onChange={(e) => setFormData((prev) => ({ ...prev, customer_item_number: e.target.value }))}
            className="rounded-sm"
            data-testid="item-customer-number-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Artwork Owner</Label>
          <Input
            placeholder="e.g., Design Team"
            value={formData.artwork_owner}
            onChange={(e) => setFormData((prev) => ({ ...prev, artwork_owner: e.target.value }))}
            className="rounded-sm"
            data-testid="item-artwork-owner-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Item Image</Label>
          <div className="flex gap-2">
            <Input
              id="image_url"
              placeholder="Paste URL or upload file"
              value={formData.image_url}
              onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))}
              className="rounded-sm flex-1"
              data-testid="item-image-input"
            />
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-sm"
              data-testid="upload-image-button"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Paste URL or upload image (max 5MB, JPEG/PNG/GIF/WebP)</p>
        </div>
      </div>

      {/* Image Preview */}
      {formData.image_url && (
        <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-sm">
          <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center overflow-hidden">
            <img 
              src={formData.image_url.startsWith("/api") ? `${API.replace("/api", "")}${formData.image_url}` : formData.image_url} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
          <div className="flex-1">
            <span className="text-sm text-muted-foreground">Image Preview</span>
            {formData.image_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-2 h-6 text-xs text-destructive hover:text-destructive"
                onClick={() => setFormData((prev) => ({ ...prev, image_url: "" }))}
              >
                <X className="w-3 h-3 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Item description..."
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          className="rounded-sm"
          rows={2}
          data-testid="item-description-input"
        />
      </div>

      <Separator />

      {/* Specifications */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Specifications</Label>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Length (cm)</Label>
            <Input
              type="number"
              value={formData.specifications.length}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, length: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Width (cm)</Label>
            <Input
              type="number"
              value={formData.specifications.width}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, width: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Height (cm)</Label>
            <Input
              type="number"
              value={formData.specifications.height}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, height: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">GSM</Label>
            <Input
              type="number"
              value={formData.specifications.gsm}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, gsm: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Material</Label>
            <Input
              value={formData.specifications.material}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, material: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Color</Label>
            <Input
              value={formData.specifications.color}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, color: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Printing Type</Label>
            <Select
              value={formData.specifications.printing_type}
              onValueChange={(v) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, printing_type: v }
              }))}
            >
              <SelectTrigger className="rounded-sm h-8">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="offset">Offset</SelectItem>
                <SelectItem value="gravure">Gravure</SelectItem>
                <SelectItem value="flexo">Flexo</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Finish</Label>
            <Select
              value={formData.specifications.finish}
              onValueChange={(v) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, finish: v }
              }))}
            >
              <SelectTrigger className="rounded-sm h-8">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="matte">Matte</SelectItem>
                <SelectItem value="gloss">Gloss</SelectItem>
                <SelectItem value="soft_touch">Soft Touch</SelectItem>
                <SelectItem value="none">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Handle Type</Label>
            <Input
              value={formData.specifications.handle_type}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                specifications: { ...prev.specifications, handle_type: e.target.value }
              }))}
              className="rounded-sm h-8"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Custom Fields */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Custom Fields</Label>
        
        {/* Existing custom fields */}
        {Object.entries(formData.custom_fields).length > 0 && (
          <div className="space-y-2 mb-3">
            {Object.entries(formData.custom_fields).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 p-2 bg-muted/30 rounded-sm">
                <span className="text-sm font-medium">{key}:</span>
                <span className="text-sm flex-1">{value}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => removeCustomField(key)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add new custom field */}
        <div className="flex gap-2">
          <Input
            placeholder="Field name"
            value={newCustomField.key}
            onChange={(e) => setNewCustomField((prev) => ({ ...prev, key: e.target.value }))}
            className="rounded-sm h-8 flex-1"
          />
          <Input
            placeholder="Value"
            value={newCustomField.value}
            onChange={(e) => setNewCustomField((prev) => ({ ...prev, value: e.target.value }))}
            className="rounded-sm h-8 flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={addCustomField} className="h-8">
            <PlusCircle className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Separator />

      {/* Remarks */}
      <div className="space-y-2">
        <Label htmlFor="remarks">Remarks / Notes</Label>
        <Textarea
          id="remarks"
          placeholder="Specific notes for this item..."
          value={formData.remarks}
          onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
          className="rounded-sm"
          rows={3}
          data-testid="item-remarks-input"
        />
      </div>
    </div>
  );

  if (loading) {
    return <ItemsSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="items-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Items</h1>
          <p className="text-muted-foreground mt-1">
            Manage product items for orders
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="new-item-button">
              <Plus className="w-4 h-4 mr-2" />
              New Item
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Item</DialogTitle>
              <DialogDescription>
                Add a new product item with specifications and custom fields.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              {renderFormFields()}
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-item-button">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Item"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingItem(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Item</DialogTitle>
            <DialogDescription>
              Update item details, specifications, and custom fields.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateItem}>
            {renderFormFields()}
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="update-item-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search + Category Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="item-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px] rounded-sm" data-testid="item-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.category_id} value={cat.category_id}>
                {cat.name} ({cat.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => (
          <Card 
            key={item.item_id} 
            className="border border-border shadow-none rounded-sm hover:bg-muted/30 transition-colors overflow-hidden"
            data-testid={`item-card-${item.item_id}`}
          >
            <CardContent className="p-4">
              <div className="flex gap-3">
                {/* Item Image */}
                <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {item.image_url ? (
                    <img 
                      src={item.image_url.startsWith("/api") ? `${API.replace("/api", "")}${item.image_url}` : item.image_url} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <ImageIcon className={`w-6 h-6 text-muted-foreground/50 ${item.image_url ? 'hidden' : ''}`} />
                </div>
                
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate" title={item.name}>{item.name}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">{item.sku}</p>
                    </div>
                    <div className="flex items-center flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDuplicateItem(item)}
                        title="Duplicate Item"
                        data-testid={`duplicate-item-${item.item_id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEditItem(item)}
                        title="Edit Item"
                        data-testid={`edit-item-${item.item_id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      {isAdmin && (
                        isSuperAdmin ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                title="Delete Item"
                                data-testid={`delete-item-${item.item_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Item</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{item.name}"? This action cannot be undone.
                                  <br /><br />
                                  <strong>Note:</strong> Items used in active orders cannot be deleted.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => handleDeleteItem(item.item_id, item.name)}
                                  disabled={deleting === item.item_id}
                                >
                                  {deleting === item.item_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  ) : null}
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-amber-600 hover:text-amber-700"
                            onClick={() => handleRequestDeletion(item.item_id, item.name)}
                            disabled={deleting === item.item_id}
                            title="Request Deletion"
                            data-testid={`request-delete-item-${item.item_id}`}
                          >
                            {deleting === item.item_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-1 mt-1.5">
                    {item.customer_name && (
                      <Badge variant="outline" className="text-[10px] max-w-[120px] truncate">
                        {item.customer_name}
                      </Badge>
                    )}
                    {(() => {
                      const catName = item.category_id
                        ? categories.find(c => c.category_id === item.category_id)?.name
                        : null;
                      return catName ? (
                        <Badge variant="secondary" className="text-[10px] max-w-[100px] truncate">
                          {catName}
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
              
              {item.remarks && (
                <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border line-clamp-2 break-words">
                  {item.remarks}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">No items found</p>
        </div>
      )}
    </div>
  );
}

const ItemsSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <Skeleton className="h-10 w-24" />
      <Skeleton className="h-10 w-28" />
    </div>
    <Skeleton className="h-10 w-full max-w-md" />
    <div className="grid grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <Skeleton key={i} className="h-40" />
      ))}
    </div>
  </div>
);
